// src/pages/booking/BookingPaymentPage.jsx

import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";
import { socket } from "../../utils/socket";
import {
  Download,
  CheckCircle,
  Clock,
  AlertCircle,
  TicketX,
} from "lucide-react";
import ErrorState from "../../components/ErrorState";
import "./booking-payment.scss";

export default function BookingPaymentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qrRef = useRef(null);
  const countdownRef = useRef(null);
  const audioRef = useRef(null);

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("PENDING");
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [socketConnected, setSocketConnected] = useState(true);

  /* ================= FETCH BOOKING ================= */
  useEffect(() => {
    let isMounted = true;

    async function fetchBooking() {
      try {
        const res = await api.get(`/api/bookings/${id}`);
        const data = res.data?.data;
        if (!isMounted || !data) return;

        setBooking(data);
        setStatus(data.paymentStatus);

        if (data.paymentStatus === "PAID") {
          setTimeout(() => {
            navigate(`/booking/${id}/ticket`, { replace: true });
          }, 800);
          return;
        }

        if (data.paymentStatus === "EXPIRED") {
          setStatus("EXPIRED");
          return;
        }

        /* 🔥 FIX PROGRESS REFRESH BUG */
        if (data.expiresAt && data.createdAt) {
          const now = Date.now();
          const expiry = new Date(data.expiresAt).getTime();
          const created = new Date(data.createdAt).getTime();

          const total = Math.floor((expiry - created) / 1000);
          const remaining = Math.floor((expiry - now) / 1000);

          if (remaining <= 0) {
            setTimeLeft(0);
            setTotalDuration(total);
            setStatus("EXPIRED");
          } else {
            setTimeLeft(remaining);
            setTotalDuration(total);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchBooking();
    return () => (isMounted = false);
  }, [id, navigate]);

  /* ================= COUNTDOWN ================= */
  useEffect(() => {
    if (!booking?.expiresAt) return;

    countdownRef.current = setInterval(() => {
      const diff =
        new Date(booking.expiresAt).getTime() - Date.now();

      if (diff <= 0) {
        clearInterval(countdownRef.current);
        setTimeLeft(0);
        setStatus("EXPIRED");
      } else {
        setTimeLeft(Math.floor(diff / 1000));
      }
    }, 1000);

    return () => clearInterval(countdownRef.current);
  }, [booking]);

  /* ================= SOCKET ================= */
  useEffect(() => {
    socket.connect();

    const handleConnect = () => {
      setSocketConnected(true);
      socket.emit("join-booking-room", id);
    };

    const handleDisconnect = () => {
      setSocketConnected(false);
    };

    const handlePaymentUpdate = (payload) => {
      if (String(payload.bookingId) !== String(id)) return;

      if (payload.paymentStatus === "PAID") {
        setStatus("PAID");

        if (audioRef.current) {
          audioRef.current.play().catch(() => {});
        }

        setTimeout(() => {
          navigate(`/booking/${id}/ticket`);
        }, 1500);
      }

      if (payload.paymentStatus === "FAILED") {
        setStatus("FAILED");
      }
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("payment-updated", handlePaymentUpdate);

    return () => {
      socket.emit("leave-booking-room", id);
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("payment-updated", handlePaymentUpdate);
    };
  }, [id, navigate]);

  /* ================= POLLING FALLBACK ================= */
  useEffect(() => {
    if (status !== "PENDING") return;

    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/api/bookings/${id}`);
        const s = res.data?.data?.paymentStatus;
        if (s === "PAID") {
          setStatus("PAID");
          clearInterval(interval);
          setTimeout(() => navigate(`/booking/${id}/ticket`), 1500);
        } else if (s === "EXPIRED" || s === "FAILED") {
          setStatus(s);
          clearInterval(interval);
        }
      } catch (_) {}
    }, 5000);

    return () => clearInterval(interval);
  }, [status, id, navigate]);

  /* ================= BEFORE UNLOAD ================= */
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (status === "PENDING") {
        e.preventDefault();
        e.returnValue =
          "Pembayaran belum selesai. Yakin ingin meninggalkan halaman?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () =>
      window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [status]);

  function downloadQr() {
    if (!qrRef.current) return;
    const link = document.createElement("a");
    link.href = qrRef.current.src;
    link.download = `QRIS-${id}.png`;
    link.click();
  }

  if (loading)
    return <div className="payment-page__state">Memuat pembayaran...</div>;

  if (!booking)
    return (
      <ErrorState
        icon={TicketX}
        title="Booking Tidak Ditemukan"
        message="Sesi booking tidak ditemukan atau sudah kedaluwarsa."
        action={{ label: "Kembali ke Beranda", href: "/" }}
      />
    );

  const minutes = Math.floor(timeLeft / 60);
  const seconds = (timeLeft % 60).toString().padStart(2, "0");

  const progress =
    totalDuration > 0
      ? Math.min(
          100,
          ((totalDuration - timeLeft) / totalDuration) * 100
        )
      : 100;

  const isDanger = timeLeft <= 120;

  return (
    <div className="payment-page">
      <audio ref={audioRef} src="/payment-success.mp3" preload="auto" />

      <div className="payment-card">
        <h2>Selesaikan Pembayaran</h2>

        {!socketConnected && (
          <div className="socket-warning">
            Koneksi terputus. Menghubungkan kembali...
          </div>
        )}

        <div
          className={`payment-status ${
            status === "PENDING" ? "pulse" : ""
          } ${isDanger ? "danger" : ""}`}
        >
          {status === "PENDING" && (
            <>
              <Clock size={16} />
              Selesaikan dalam{" "}
              <strong>
                {minutes}:{seconds}
              </strong>
            </>
          )}

          {status === "PAID" && (
            <>
              <CheckCircle size={16} />
              Pembayaran Berhasil 🎉
              <br />
              Tiket sudah dikirim ke email kamu.
            </>
          )}

          {(status === "EXPIRED" || status === "FAILED") && (
            <>
              <AlertCircle size={16} />
              Pembayaran Tidak Berhasil
            </>
          )}
        </div>

        {status === "PENDING" && (
          <div className="countdown-bar">
            <div
              className={`countdown-fill ${
                isDanger ? "danger" : ""
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {status === "PENDING" && (
          <>
            <div className="qr-container">
              <img
                ref={qrRef}
                src={booking.qr?.imageUrl}
                alt="QRIS"
              />
            </div>

            <button
              className="download-btn"
              onClick={downloadQr}
            >
              <Download size={16} />
              Download QRIS
            </button>

            {/* 🔥 QRIS INSTRUCTIONS */}
            <div className="qris-instruction">
              <h4>Cara Pembayaran QRIS</h4>
              <ol>
                <li>Buka aplikasi e-wallet atau mobile banking kamu.</li>
                <li>Pilih menu Scan / QRIS.</li>
                <li>Arahkan kamera ke kode QR di atas.</li>
                <li>Pastikan nominal sesuai.</li>
                <li>Konfirmasi dan selesaikan pembayaran.</li>
              </ol>
              <p>
                Setelah berhasil, tiket akan otomatis aktif tanpa perlu refresh.
              </p>
            </div>
          </>
        )}

        {(status === "EXPIRED" || status === "FAILED") && (
          <button
            className="retry-btn"
            onClick={() =>
              navigate(`/events/${booking.event.slug}`)
            }
          >
            Pesan Ulang Tiket
          </button>
        )}

        <div className="payment-detail">
          <h4>Detail Pembayaran</h4>
          <div>
            <span>ID Booking</span>
            <strong>{booking._id}</strong>
          </div>
          <div>
            <span>Total</span>
            <strong>
              Rp{" "}
              {booking.ticket.total.toLocaleString("id-ID")}
            </strong>
          </div>
        </div>

        <div className="warning-text">
          Jangan tutup halaman ini sebelum pembayaran selesai.
        </div>
      </div>
    </div>
  );
}